import React, { useContext } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { passwordSchema, PasswordInput } from '@carvana/showroom-forms';
import * as Yup from 'yup';
import PageTitle from '../../../shared/components/PageTitle/PageTitle';
import { TabContent } from '../../../routes/RouterOutlet.styles';
import FormButtons from '../../../shared/components/FormButtons';
import { editPasswordSelectors, editPasswordOperations } from './ducks';
import type { RootState } from '../../../store/types';
import { Row, Column } from '../../../shared/components/Layout';
import { FormWrapper } from './EditPassword.styles';
import EditPasswordDisclaimer from './EditPasswordDisclaimer/EditPasswordDisclaimer';
import { getDisplayNameByPath, validatePassword } from '../../../utilities';
import { personalDetailsSelectors } from '../ducks';

const formValidationSchema = Yup.object().shape({
  currentPassword: passwordSchema.login,
  newPassword: passwordSchema.create,
  newPasswordConfirm: passwordSchema.confirm('newPassword')
});

type OwnProps = {
  location?: { pathname: string };
};

type StateProps = {
  emailAddress: string;
  isSubmitting: boolean;
  isUpdatingPassword: boolean;
  sectionName: string;
};

type DispatchProps = {
  updatePassword: (
    credentials: { currentPassword: string; newPassword: string },
    sectionName: string
  ) => void;
};

function EditPassword({
  updatePassword,
  isUpdatingPassword,
  sectionName,
  isSubmitting,
  emailAddress
}: OwnProps & StateProps & DispatchProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const passwordEventName = 'user platform - change password - password strength validation error';

  return (
    <>
      <PageTitle title="Edit Password" />
      <TabContent>
        <Formik
          initialValues={{
            currentPassword: '',
            newPassword: '',
            newPasswordConfirm: ''
          }}
          validationSchema={formValidationSchema}
          onSubmit={(values, { setErrors }) => {
            const { currentPassword, newPassword } = values;
            const { passwordIsInvalid, errorMessage, failureType } = validatePassword(
              newPassword,
              emailAddress
            );

            if (passwordIsInvalid) {
              setErrors({ newPassword: errorMessage });
              trackEvent(passwordEventName, { FailureType: failureType, Email: emailAddress });
              return;
            }

            updatePassword({ currentPassword, newPassword }, sectionName);
          }}
          render={({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => {
            const isSubmitDisabled = !!(
              isUpdatingPassword ||
              !touched.newPassword ||
              errors.currentPassword ||
              errors.newPassword ||
              errors.newPasswordConfirm
            );
            return (
              <FormWrapper onSubmit={handleSubmit}>
                <Row>
                  <EditPasswordDisclaimer />
                </Row>
                <Row>
                  <Column>
                    <PasswordInput
                      name="currentPassword"
                      id="current-password"
                      variant="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.currentPassword}
                      touched={touched.currentPassword}
                      error={errors.currentPassword}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <PasswordInput
                      name="newPassword"
                      id="new-password"
                      label="New Password"
                      variant="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.newPassword}
                      touched={touched.newPassword}
                      error={errors.newPassword}
                      description="Password must be a minimum of 8 characters"
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <PasswordInput
                      name="newPasswordConfirm"
                      id="new-password-confirm"
                      label="Confirm New Password"
                      variant="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.newPasswordConfirm}
                      touched={touched.newPasswordConfirm}
                      error={errors.newPasswordConfirm}
                    />
                  </Column>
                </Row>
                <FormButtons isSubmitting={isSubmitting} isSubmitDisabled={isSubmitDisabled} />
              </FormWrapper>
            );
          }}
        />
      </TabContent>
    </>
  );
}

const mapDispatchToProps = {
  updatePassword: editPasswordOperations.updatePassword
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  isUpdatingPassword: editPasswordSelectors.getIsUpdatingPassword(state),
  sectionName: getDisplayNameByPath(ownProps?.location?.pathname ?? ''),
  isSubmitting: personalDetailsSelectors.getUpdatePasswordIsLoading(state),
  emailAddress: personalDetailsSelectors.getEmailAddress(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPassword);
